import React from 'react';
import { useFormContext, UseFormMethods } from 'react-hook-form';
import type { FieldError } from 'react-hook-form/dist/types/errors';

import { resolveNode } from 'utils/object';
import { FormField } from 'ui/forms/custom/types';
import { useTranslationFieldOrDefault } from 'hooks/useTranslationFieldOrDefault';
import { useCheckoutFormData } from 'hooks/useCheckoutFormData';
import type { CheckoutFormHookReturnType } from 'hooks/useCheckoutFormData';
import { AddressFormValues } from 'ui/forms/CheckoutAddressForm';

/**
 * A safe wrapper around useCheckoutFormData that provides mock functionality
 * when the CheckoutFormProvider is not available (e.g., in Storybook).
 *
 * This hook catches the error thrown by useCheckoutFormData when used outside
 * of a CheckoutFormProvider and returns mock implementations of the functions.
 *
 * @template T The form values type
 * @param addressForm Optional form methods from react-hook-form
 * @returns The same interface as useCheckoutFormData
 */
export const useSafeCheckoutFormData = <
  T extends Record<string, unknown> = Record<string, unknown>
>(
  addressForm?: UseFormMethods<T>
): CheckoutFormHookReturnType => {
  try {
    // The original hook only uses the form methods, not the generic type parameter
    // This is type-safe because we're only passing the form methods object structure
    // Using unknown instead of any to maintain type safety
    return useCheckoutFormData(
      addressForm as unknown as UseFormMethods<AddressFormValues>
    );
  } catch (error) {
    // Return mock implementations if the hook throws
    return {
      getPersistedFormField: (_fieldName: string): string | boolean =>
        typeof _fieldName === 'string' && _fieldName.includes('useAsBilling')
          ? false
          : '',
      setPersistedFormField: (
        _fieldName: string,
        _value: string | boolean
      ): void => {
        // No-op implementation
      },
      clearPersistedFormField: (_fieldName: string): void => {
        // No-op implementation
      },
      clearAddressFormFields: (
        _addressType: 'shippingAddress' | 'billingAddress'
      ): void => {
        // No-op implementation
      },
      data: {} as Record<string, string | boolean>,
    };
  }
};

export const useFormField = (field: FormField) => {
  const { errors } = useFormContext();

  const getErrorMessageOrDefault = useTranslationFieldOrDefault();

  const resolveInputError = React.useCallback(
    () => resolveNode<FieldError>(field.name, errors)?.message,
    [field, errors]
  );
  return {
    getErrorMessageOrDefault,
    resolveInputError,
  };
};
